import { NavLink } from "react-router-dom";
import styled, { css } from "styled-components";
import { Slide } from "react-awesome-reveal";

interface StyleProps {
  isScrolled?: boolean;
  isValidRoute?: boolean;
  isResumePage?: boolean;
  variant?: 'light' | 'dark';
}

interface IsMobileProps {
  isMobile?: boolean;
}

export const Nav = styled.nav<StyleProps>(
  ({ isValidRoute, isResumePage, variant = 'light' }: StyleProps) => css`
    display: flex;
    justify-content: space-between;
    padding: 24px clamp(20px, 4.5vw, 64px);
    height: 88px;
    align-items: center;
    position: sticky;
    top: 0;
    transition: all 0.2s ease 0s;
    z-index: 2;

    ${variant === 'dark' ? css`
      background-color: #1A1814 !important;
      border-bottom: 1px solid #3A342C !important;
    ` : css`
      background-color: #F3ECE1;
      border-bottom: ${isValidRoute || isResumePage ? "none" : "1px solid #D2C8B8"};
    `}
  `
);

export const MobileNavbar = styled(Nav)`
  flex-direction: column;
`;

export const LogoContainer = styled.div``;

export const NavLinks = styled.div<StyleProps>(
  ({ isValidRoute, variant = 'light' }: StyleProps) => css`
    display: flex;
    align-items: center;
    position: relative;
    gap: 40px;
    margin-left: auto;
    font-family: 'IBM Plex Mono', ui-monospace, Menlo, monospace;
    font-size: 11.5px;
    font-weight: 400;
    letter-spacing: 0.18em;
    text-transform: uppercase;

    ${variant === 'dark' ? css`
      color: #F3ECE1 !important;
    ` : css`
      color: #1A1814;
    `}
  `
);

export const MenuItem = styled(NavLink)<StyleProps>(
  ({ isScrolled, isValidRoute, variant = 'light' }: StyleProps) => css`
    font: 400 11.5px 'IBM Plex Mono', ui-monospace, Menlo, monospace;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    padding: 0;
    margin: 0;
    text-decoration: none;
    transition: all 0.3s ease-in-out;

    ${variant === 'dark' ? css`
      color: #A89E8F !important;

      &:hover {
        color: #F3ECE1 !important;
      }

      &.guide {
        color: #F3ECE1 !important;
      }

      &.cta {
        color: #F3ECE1 !important;
        border-bottom: 1px solid #F3ECE1 !important;
        padding-bottom: 3px;
      }
    ` : css`
      color: #1A1814;

      &:hover {
        color: #D9442B;
      }

      &.cta {
        color: #1A1814;
        border-bottom: 1px solid #1A1814;
        padding-bottom: 3px;
      }
    `}

    &.active {
      color: #D9442B;
    }

    &.guide::before {
      content: '';
      display: inline-block;
      width: 6px;
      height: 6px;
      margin-right: 8px;
      border-radius: 50%;
      background: #D9442B;
      vertical-align: 2px;
    }
  `
);

export const ScrollMenuItem = styled.a<StyleProps>(
  ({ isScrolled, isValidRoute }: StyleProps) => css`
    font-family: 'Newsreader', Georgia, serif;
    font-size: 16px;
    font-weight: 400;
    margin: 0 16px;
    padding: 8px 4px;
    text-decoration: none;
    color: #1A1814;
    transition: all 0.3s ease-in-out;
    cursor: pointer;

    &:hover {
      color: #D9442B;
    }

    @media screen and (max-width: 1024px) {
      margin: 0 12px;
    }
  `
);

export const LogoNavLink = styled(MenuItem)<StyleProps>(
  ({ variant = 'light' }: StyleProps) => css`
    font-family: 'Instrument Serif', Georgia, 'Times New Roman', serif;
    font-size: 30px;
    font-weight: 400;
    letter-spacing: -0.01em;
    margin: 0;
    padding: 0;
    text-transform: none;

    ${variant === 'dark' ? css`
      color: #F3ECE1 !important;

      &:hover, &.active {
        color: #F3ECE1 !important;
        opacity: 1;
      }
    ` : css`
      color: #1A1814;

      &:hover, &.active {
        color: #1A1814;
        opacity: 1;
      }
    `}

    &::before {
      display: none;
    }
  `
);

export const MobileMenuItem = styled(NavLink)`
  text-decoration: none;
  color: white;
  font-size: 1.5rem;
  font-weight: 500;
  width: 100%;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(217, 68, 43, 0.1);
  }

  &.active {
    color: #D9442B;
  }
`;

export const MobileScrollMenuItem = styled.a`
  text-decoration: none;
  color: white;
  font-size: 1.5rem;
  font-weight: 500;
  width: 100%;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  transition: background-color 0.2s ease;
  cursor: pointer;
  display: block;

  &:hover {
    background-color: rgba(217, 68, 43, 0.1);
  }
`;

export const MobileDropdownMenuItem = styled(MobileMenuItem)`
  background-color: #1A1814;
`;

export const ServicesMenuItem = styled(MobileMenuItem)`
  border-bottom: unset;
  padding: 24px 0px 24px 36px;
`;

export const Logo = styled.img`
  width: 200px;
`;

export const MobileNavLinks = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #1A1814;
  height: calc(100vh - 60px);
  height: calc(100dvh - 60px);
  width: 100%;
  overflow: hidden;
`;

export const SocialLinks = styled.div`
  display: flex;
  gap: 12px;
  padding: 24px;
  padding-bottom: calc(24px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  justify-content: center;
`;

export const SocialLink = styled.img`
  width: 24px;
  height: 24px;
  cursor: pointer;
  filter: brightness(0) invert(1);
  opacity: 0.7;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }
`;

export const MobileNavIconButton = styled.button`
  background-color: unset;
  border: none;
`;

export const SocialMediaButton = styled.button`
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(217, 68, 43, 0.2);
    border-color: rgba(217, 68, 43, 0.4);
  }
`;

export const DownArrow = styled.img`
  rotate: 180deg;
  width: 16px;
  cursor: pointer;
`;

export const UpArrow = styled(DownArrow)`
  rotate: unset;
`;

export const DesktopDownArrow = styled(DownArrow)`
  width: 12px;
`;

export const DesktopUpArrow = styled(DesktopDownArrow)`
  rotate: unset;
`;

export const MenuItemDropDownContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

export const MenuItemAndIcon = styled.div<StyleProps>(
  ({ isScrolled }: StyleProps) => css`
    display: flex;
    flex-direction: row;
    justify-content: center;
    border-bottom: ${isScrolled ? "0px" : "1px solid rgba(0, 0, 0, 0.05)"};
    align-items: center;
  `
);

export const Button = styled.button<IsMobileProps>(
  ({ isMobile }: IsMobileProps) => css`
    border: none;
    background: none;
    width: fit-content;
    margin: ${isMobile ? "24px 36px" : "unset"};
    padding: 36px;
    cursor: pointer;
  `
);

export const DesktopButton = styled(Button)`
  padding: 0px;
  margin-right: 24px;
`;

export const ServicePagesNavLinks = styled(Slide)`
  display: flex;
  flex-direction: column;
  z-index: 1;
`;

export const DropDownLinks = styled.div<StyleProps>(
  ({ isScrolled, isValidRoute }: StyleProps) => css`
    position: absolute;
    top: 100%;
    display: flex;
    flex-direction: column;
    background: #1A1814;
    border-radius: 0px 0px 4px 4px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-top: none;
  `
);
